// Datenzugriff fürs Mietkonto. Solange die Tabellen aus dem Migrationsentwurf
// (docs/db/entwurf/2026-10-12-mietkonto-ENTWURF.sql) nicht angelegt sind, meldet
// ladeMietkontoDaten „nicht eingerichtet“ – die Oberfläche zeigt dann einen Leerzustand
// statt falscher Rückstände.

import { supabase } from '../supabase/client';
import type { HausgeldRoh, MietverhaeltnisRoh } from './soll';
import type { Buchung, LedgerEntry, Quelle } from './typen';
import { mieterKonten, type EinheitKurz } from './mietkonto';
import { alleSeiten } from '../supabase/alleSeiten';

interface DbFehler { code?: string; message: string }

/** Tabelle/Spalte existiert (noch) nicht – PostgREST-Schema-Cache oder Postgres. */
export function istNichtEingerichtet(f: DbFehler | null | undefined): boolean {
  if (!f) return false;
  return f.code === 'PGRST205' || f.code === '42P01' || f.code === 'PGRST204'
    || /does not exist|could not find the table|schema cache/i.test(f.message);
}

export interface MietkontoDaten {
  verwaltungsart: string | null;
  einheiten: EinheitKurz[];
  mieter: (MietverhaeltnisRoh & { vorname: string | null; nachname: string | null; firma_name: string | null; mieter_iban: string | null })[];
  hausgeld: (HausgeldRoh & { name: string; eigentuemer_id: string | null })[];
  buchungen: Buchung[];
  start: string;
}

export type MietkontoLaden =
  | { status: 'nicht_eingerichtet' }
  | { status: 'fehler'; meldung: string }
  | { status: 'ok'; daten: MietkontoDaten };

const MIETER_FELDER = 'id, einheit_id, status, vorname, nachname, firma_name, kaltmiete, nebenkosten_vorauszahlung, heizkosten, weitere_kosten, miet_historie, mietbeginn, mietende, miete_anteilig_beginn, miete_anteilig_ende, miete_faellig_tag, anfangssaldo_aktiv, anfangssaldo_betrag, anfangssaldo_faelligkeit, mieter_iban';

/** Startmonat aus app_settings (jsonb-String „YYYY-MM-DD“); ohne Eintrag: nicht eingerichtet. */
async function ladeStart(): Promise<string | null> {
  const { data } = await supabase.from('app_settings').select('value').eq('key', 'mietkonto_start').maybeSingle();
  const wert = (data as { value: unknown } | null)?.value;
  return typeof wert === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(wert) ? wert : null;
}

const BUCHUNG_FELDER = 'id, konto_schluessel, einheit_id, datum, betrag, quelle, zahler_name, storniert_am';
type BuchRoh = { id: string; konto_schluessel: string; einheit_id: string; datum: string; betrag: number | string; quelle: Quelle; zahler_name: string; storniert_am: string | null };
const zuBuchung = (b: BuchRoh): Buchung => ({
  id: b.id, kontoSchluessel: b.konto_schluessel, einheitId: b.einheit_id, datum: b.datum, betrag: Number(b.betrag), quelle: b.quelle, zahler: b.zahler_name, storniert: !!b.storniert_am,
});

export async function ladeMietkontoDaten(objektId: string): Promise<MietkontoLaden> {
  // Ohne Startmonat ist das Mietkonto nicht eingerichtet – dann die Tabelle gar nicht erst abfragen.
  const start = await ladeStart();
  if (!start) return { status: 'nicht_eingerichtet' };
  const [buch, obj, ein, mie] = await Promise.all([
    supabase.from('mietkonto_buchungen')
      .select(BUCHUNG_FELDER)
      .eq('objekt_id', objektId).order('datum', { ascending: true }),
    supabase.from('objekte').select('verwaltungsart, objekttyp').eq('id', objektId).maybeSingle(),
    supabase.from('objekt_einheiten').select('id, bezeichnung').eq('objekt_id', objektId),
    supabase.from('mieter').select(MIETER_FELDER).eq('objekt_id', objektId),
  ]);
  if (istNichtEingerichtet(buch.error)) return { status: 'nicht_eingerichtet' };
  const fehler = buch.error ?? obj.error ?? ein.error ?? mie.error;
  if (fehler) return { status: 'fehler', meldung: fehler.message };

  const einheiten = (ein.data ?? []) as EinheitKurz[];
  let hausgeld: MietkontoDaten['hausgeld'] = [];
  if (einheiten.length) {
    type Roh = HausgeldRoh & { eigentuemer_id: string | null; eigentuemer: { vorname: string | null; nachname: string | null; firma_name: string | null } | null };
    const hg = await supabase.from('einheit_eigentuemer')
      .select('einheit_id, eigentuemer_id, hausgeld_vorschuss, ruecklage_vorschuss, vertrag_ab, vertrag_bis, eigentuemer:eigentuemer_id(vorname, nachname, firma_name)')
      .in('einheit_id', einheiten.map(e => e.id));
    if (hg.error) return { status: 'fehler', meldung: hg.error.message };
    hausgeld = ((hg.data ?? []) as unknown as Roh[]).map(h => ({
      ...h,
      name: h.eigentuemer ? ((h.eigentuemer.firma_name ?? '').trim() || `${h.eigentuemer.vorname ?? ''} ${h.eigentuemer.nachname ?? ''}`.trim()) : '',
    }));
  }

  const objekt = obj.data as { verwaltungsart: string | null; objekttyp: string | null } | null;
  return {
    status: 'ok',
    daten: {
      verwaltungsart: objekt?.verwaltungsart ?? (/weg|teileigentum/i.test(objekt?.objekttyp ?? '') ? 'WEG' : null),
      einheiten,
      mieter: (mie.data ?? []) as MietkontoDaten['mieter'],
      hausgeld,
      buchungen: ((buch.data ?? []) as BuchRoh[]).map(zuBuchung),
      start,
    },
  };
}

export type MieterKontenLaden =
  | { status: 'nicht_eingerichtet' }
  | { status: 'fehler'; meldung: string }
  | { status: 'ok'; konten: Map<string, LedgerEntry> };

/** Mietkonten aller Mieter (Mieterliste): eine Ladung über alle Objekte, seitenweise – keine Abfrage je Objekt. */
export async function ladeMieterKonten(heute: string): Promise<MieterKontenLaden> {
  // Ohne Startmonat ist das Mietkonto nicht eingerichtet – dann die Tabelle gar nicht erst abfragen.
  const start = await ladeStart();
  if (!start) return { status: 'nicht_eingerichtet' };
  const probe = await supabase.from('mietkonto_buchungen').select('id').limit(1);
  if (istNichtEingerichtet(probe.error)) return { status: 'nicht_eingerichtet' };
  if (probe.error) return { status: 'fehler', meldung: probe.error.message };
  try {
    const [buchungen, einheiten, mieter] = await Promise.all([
      alleSeiten<BuchRoh>((von, bis) => supabase.from('mietkonto_buchungen').select(BUCHUNG_FELDER).like('konto_schluessel', 'mieter:%').order('id').range(von, bis)),
      alleSeiten<EinheitKurz & { objekt_id: string }>((von, bis) => supabase.from('objekt_einheiten').select('id, bezeichnung, objekt_id').order('id').range(von, bis)),
      alleSeiten<MietkontoDaten['mieter'][number]>((von, bis) => supabase.from('mieter').select(MIETER_FELDER).order('id').range(von, bis)),
    ]);
    return { status: 'ok', konten: mieterKonten({ einheiten, mieter, buchungen: buchungen.map(zuBuchung), start, heute }) };
  } catch (e) {
    return { status: 'fehler', meldung: e instanceof Error ? e.message : 'Mietkonto konnte nicht geladen werden' };
  }
}

export interface NeueZahlung {
  objektId: string;
  /** Konto des Mietverhältnisses (mieterKonto / eigentuemerKonto) */
  kontoSchluessel: string;
  einheitId: string;
  mieterId?: string | null;
  eigentuemerId?: string | null;
  zahlerName: string;
  datum: string;
  betrag: number;
  quelle: Quelle;
  bankUmsatzId?: string | null;
  notiz?: string | null;
}

/** Jede Zahlung – egal aus welchem Bereich – wird hier gebucht (README §6 Regel 2). */
export async function bucheZahlung(z: NeueZahlung): Promise<string> {
  if (!(z.betrag > 0)) throw new Error('Der Betrag muss größer als 0 sein.');
  const { data, error } = await supabase.from('mietkonto_buchungen').insert({
    objekt_id: z.objektId, konto_schluessel: z.kontoSchluessel, einheit_id: z.einheitId, mieter_id: z.mieterId ?? null, eigentuemer_id: z.eigentuemerId ?? null,
    zahler_name: z.zahlerName, datum: z.datum, betrag: Math.round(z.betrag * 100) / 100, quelle: z.quelle,
    bank_umsatz_id: z.bankUmsatzId ?? null, notiz: z.notiz ?? null,
  }).select('id').single();
  if (error) throw new Error(error.message);
  return (data as { id: string }).id;
}

/** Storno statt Löschen (z. B. „Bezahlt rückgängig“ im Mieteingang, README §6 Regel 5). */
export async function storniereZahlung(buchungId: string, nutzerId: string, grund: string): Promise<void> {
  const { error } = await supabase.from('mietkonto_buchungen')
    .update({ storniert_am: new Date().toISOString(), storniert_von: nutzerId, storno_grund: grund })
    .eq('id', buchungId).is('storniert_am', null);
  if (error) throw new Error(error.message);
}
